import type { MutableRefObject } from "react";
import type { PermissionLevel } from "../lib/permission-levels";
import {
  buildFrontendDecisionContext,
  readDecisionUserContext,
} from "../lib/decision-context";
import { speakAssistantReply } from "../lib/voice-browser-runtime";
import type { DecisionContext } from "../types/decision-context";
import type { VoiceRuntimeState } from "../types/voice";
import type { StopRecordingOptions } from "./voice-listening-session";

export interface VoiceSendResult {
  conversationId?: string;
  responseText?: string;
  audioBase64?: string;
  audioMimeType?: string;
  taskId?: string;
  recoverable?: boolean;
}

export interface VoiceSendOptions {
  requestOrigin?: "user" | "system";
}

interface VoiceSpeakClient {
  voice: {
    speak(data: {
      text: string;
      mode: "chat" | "infraction";
      conversationId?: string;
      includeAudio: boolean;
      agent: string;
      permissionLevel?: PermissionLevel;
      decisionContext?: DecisionContext;
    }): Promise<VoiceSendResult>;
  };
}

export interface VoiceTextRequestInput {
  text: string;
  mode: "chat" | "infraction";
  options: VoiceSendOptions;
  backend: VoiceSpeakClient;
  conversationId: string | null;
  permissionLevel: PermissionLevel;
  busyRef: MutableRefObject<boolean>;
  abortRef: MutableRefObject<AbortController | null>;
  enabledRef: MutableRefObject<boolean>;
  replyAudioEnabledRef: MutableRefObject<boolean>;
  stopRecording: (options?: StopRecordingOptions) => void;
  persistConversationId: (id: string | null) => void;
  setHasUserRequestActivity: (active: boolean) => void;
  setLastUserText: (text: string) => void;
  setLastAssistantText: (text: string) => void;
  setRuntimeState: (state: VoiceRuntimeState) => void;
  setErrorState: () => void;
  analyzeSpeechForTilt: (transcript: string) => Promise<void>;
  restartListening: () => void;
}

export async function sendVoiceTextRequest({
  text,
  mode,
  options,
  backend,
  conversationId,
  permissionLevel,
  busyRef,
  abortRef,
  enabledRef,
  replyAudioEnabledRef,
  stopRecording,
  persistConversationId,
  setHasUserRequestActivity,
  setLastUserText,
  setLastAssistantText,
  setRuntimeState,
  setErrorState,
  analyzeSpeechForTilt,
  restartListening,
}: VoiceTextRequestInput): Promise<VoiceSendResult | null> {
  const prompt = text.trim();
  if (!prompt || busyRef.current) return null;

  stopRecording({ process: false });
  busyRef.current = true;
  if (options.requestOrigin !== "system") {
    setHasUserRequestActivity(true);
    setLastUserText(prompt);
  }
  setRuntimeState("thinking");

  const controller = new AbortController();
  abortRef.current = controller;

  try {
    const response = await backend.voice.speak({
      text: prompt,
      mode,
      conversationId: conversationId || undefined,
      includeAudio: false,
      agent: "harper-cao",
      permissionLevel,
      decisionContext: buildFrontendDecisionContext({
        surfaceId: "voice",
        conversationId,
        userContext: readDecisionUserContext(),
        userMode: mode,
        persona: "harper-cao",
      }),
    });

    if (controller.signal.aborted) return null;

    if (response.conversationId) {
      persistConversationId(response.conversationId);
    }

    const assistantText = response.responseText || "";
    setLastAssistantText(assistantText);

    if (assistantText) {
      setRuntimeState("speaking");
      await speakAssistantReply({
        text: assistantText,
        enabled: replyAudioEnabledRef.current,
        onStart: () => setRuntimeState("speaking"),
        onError: (message) => {
          console.warn("[VoiceAssistant] Native speech failed:", message);
        },
      });
    }

    if (!controller.signal.aborted) {
      if (prompt && mode === "chat") {
        analyzeSpeechForTilt(prompt).catch(() => {});
      }

      const shouldResumeListening = enabledRef.current;
      busyRef.current = false;
      setRuntimeState(shouldResumeListening ? "listening" : "idle");
      if (shouldResumeListening) {
        await new Promise((resolve) => setTimeout(resolve, 300));
        if (!controller.signal.aborted && enabledRef.current) {
          restartListening();
        }
      }
    }
    return response;
  } catch (error) {
    if (controller.signal.aborted) return null;
    console.error("[VoiceAssistant] Failed to send text:", error);
    const recovery = readVoiceRecovery(error);
    if (recovery?.conversationId) {
      persistConversationId(recovery.conversationId);
    }
    if (recovery?.recoverable) {
      setLastAssistantText("Request saved. Open Chat to retry.");
    }
    setErrorState();
    return null;
  } finally {
    busyRef.current = false;
    if (abortRef.current === controller) {
      abortRef.current = null;
    }
  }
}

function readVoiceRecovery(error: unknown): {
  conversationId: string | null;
  taskId: string | null;
  recoverable: boolean;
} | null {
  if (!error || typeof error !== "object" || !("details" in error)) {
    return null;
  }
  const details = (error as { details?: unknown }).details;
  if (!details || typeof details !== "object") return null;
  const record = details as Record<string, unknown>;
  return {
    conversationId:
      typeof record.conversationId === "string" ? record.conversationId : null,
    taskId: typeof record.taskId === "string" ? record.taskId : null,
    recoverable: record.recoverable === true,
  };
}
